import { ApiError, api } from 'shared/api/client';
import { apiErrorText } from 'shared/api/error-message';
import { useQueryClient, useMutation, useQuery, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { UUID, CalculationLinkrole, CalculationRuleSummandInput, CalculationRuleSummary } from './types';
import { taxonomyQueryKeys } from './taxonomy';
import { dispatchMessage, EventType } from 'shared/feedback/toasts';
import { i18n, type MessageKey } from 'shared/i18n';

const CALC_RULE_ERROR_LABELS: Record<string, MessageKey> = {
  CALC_RULE_BALANCE_WEIGHT: 'tag:calculations.error.balance-weight',
  CALC_RULE_CYCLE: 'tag:calculations.error.cycle',
  CALC_RULE_DUPLICATE: 'tag:calculations.error.duplicate',
  CALC_RULE_SELF_REFERENCE: 'tag:calculations.error.self-reference',
};

function calculationRuleErrorHandler(error: unknown) {
  const label = error instanceof ApiError ? CALC_RULE_ERROR_LABELS[error.code] : undefined;
  const message = label ? i18n(label) : apiErrorText(error, i18n('tag:calculations.error.generic'));
  dispatchMessage(EventType.ADD_MESSAGE, { type: 'error', text: message, removeTime: 5000 });
}

export const calculationRuleQueryKeys = {
  list: 'CALCULATION_RULES',
  linkroles: 'CALCULATION_LINKROLES',
} as const;

function invalidateCalculations(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({
    queryKey: [calculationRuleQueryKeys.list],
  });
  queryClient.invalidateQueries({
    queryKey: [taxonomyQueryKeys.calculations],
  });
}

export function useCalculationLinkroles(documentSuperId: number | undefined): UseQueryResult<CalculationLinkrole[]> {
  return useQuery({
    queryKey: [calculationRuleQueryKeys.linkroles, documentSuperId],

    queryFn: async () => {
      const res = await api.GET('/taxonomy/calculation-linkroles', { params: { query: { documentSuperId: String(documentSuperId) } } });
      return res?.data || [];
    },

    enabled: !!documentSuperId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCalculationRules(documentSuperId: number | undefined, taxonomyId?: string): UseQueryResult<CalculationRuleSummary[]> {
  return useQuery({
    queryKey: [calculationRuleQueryKeys.list, documentSuperId, taxonomyId],

    queryFn: async () => {
      const res = await api.GET('/concepts/calculation-rules/', {
        params: { query: { documentSuperId: String(documentSuperId), ...(taxonomyId ? { taxonomyId } : {}) } },
      });
      return res?.data || [];
    },

    enabled: !!documentSuperId,
  });
}

export type CreateCalculationRuleRequest = {
  documentSuperId: number;
  parentConceptId: UUID;
  linkroleUri: string | null;
  summands: CalculationRuleSummandInput[];
};

export function useCreateCalculationRule(): UseMutationResult<CalculationRuleSummary, unknown, CreateCalculationRuleRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateCalculationRuleRequest) => {
      const res = await api.POST('/concepts/calculation-rules/', { body });
      return res?.data;
    },

    onSuccess: () => invalidateCalculations(queryClient),
    onError: calculationRuleErrorHandler,
  });
}

export type SeedCalculationRuleRequest = {
  documentSuperId: number;
  parentConceptId: UUID;
  linkroleUri: string;
};

export function useSeedCalculationRule(): UseMutationResult<CalculationRuleSummary, unknown, SeedCalculationRuleRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SeedCalculationRuleRequest) => {
      const res = await api.POST('/concepts/calculation-rules/seed', { body });
      return res?.data;
    },

    onSuccess: () => invalidateCalculations(queryClient),
    onError: calculationRuleErrorHandler,
  });
}

export interface UpdateCalculationRuleRequest {
  ruleId: UUID;
  documentSuperId: number;
  summands: CalculationRuleSummandInput[];
}

export function useUpdateCalculationRule(): UseMutationResult<CalculationRuleSummary, unknown, UpdateCalculationRuleRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ruleId, ...body }: UpdateCalculationRuleRequest) => {
      const res = await api.PUT('/concepts/calculation-rules/{id}', { params: { path: { id: ruleId } }, body });
      return res?.data;
    },

    onSuccess: () => invalidateCalculations(queryClient),
    onError: calculationRuleErrorHandler,
  });
}

export interface DeleteCalculationRuleRequest {
  ruleId: UUID;
  documentSuperId: number;
}

export function useDeleteCalculationRule(): UseMutationResult<unknown, unknown, DeleteCalculationRuleRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ruleId, documentSuperId }: DeleteCalculationRuleRequest) => {
      await api.DELETE('/concepts/calculation-rules/{id}', { params: { path: { id: ruleId }, query: { documentSuperId: String(documentSuperId) } } });
    },

    onSuccess: () => invalidateCalculations(queryClient),
    onError: calculationRuleErrorHandler,
  });
}
